// Dyia, the assistant (doc 13): its platform settings and the chat exchanged with the web app.
import { z } from 'zod';
import { objectIdSchema } from './common';

export const ASSISTANT_NAME = 'Dyia';

/** `demo` answers from fixed rules without calling any AI service; it is refused in production. */
export const ASSISTANT_PROVIDERS = ['anthropic', 'demo'] as const;
export const assistantProviderSchema = z.enum(ASSISTANT_PROVIDERS);
export type AssistantProvider = z.infer<typeof assistantProviderSchema>;

export const ASSISTANT_DEFAULT_MODEL = 'claude-sonnet-5-5';
export const ASSISTANT_MODELS = ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-haiku-5-5'] as const;

// ---- Platform settings (SmarDac staff) ----

export const assistantSettingsSchema = z.object({
  enabled: z.boolean(),
  provider: assistantProviderSchema,
  /** Providers that can be chosen on this server. */
  providers: z.array(assistantProviderSchema),
  model: z.string(),
  /** The key itself is never returned. */
  hasApiKey: z.boolean(),
  maxOutputTokens: z.number(),
  dailyMessagesPerUser: z.number(),
  /** What was used over the last `days` days, all organizations together. */
  usage: z.object({ days: z.number(), messages: z.number(), inputTokens: z.number(), outputTokens: z.number() }),
});
export type AssistantSettings = z.infer<typeof assistantSettingsSchema>;

export const updateAssistantSettingsRequestSchema = z
  .object({
    enabled: z.boolean(),
    provider: assistantProviderSchema,
    model: z
      .string()
      .trim()
      .regex(/^[a-z0-9][a-z0-9.-]{2,63}$/, 'Use a model id such as "claude-sonnet-5-5"'),
    /** A new key, or null to remove the stored one. */
    apiKey: z.string().trim().min(8).max(300).nullable(),
    maxOutputTokens: z.number().int().min(256).max(8192),
    dailyMessagesPerUser: z.number().int().min(1).max(10_000),
  })
  .partial();
export type UpdateAssistantSettingsRequest = z.infer<typeof updateAssistantSettingsRequestSchema>;

// ---- Chat ----

export const assistantMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  text: z.string().trim().min(1).max(4000),
});
export type AssistantMessage = z.infer<typeof assistantMessageSchema>;

export const ASSISTANT_MAX_MESSAGES = 24;
export const assistantChatRequestSchema = z.object({
  /** The conversation so far, oldest first; the last message is the question of the user. */
  messages: z.array(assistantMessageSchema).min(1).max(ASSISTANT_MAX_MESSAGES),
  /** Page the user is looking at, e.g. "/o/<orgId>/dashboards". */
  page: z.string().max(300).optional(),
  /** Language of the interface, e.g. "fr". */
  language: z.string().max(16).optional(),
});
export type AssistantChatRequest = z.infer<typeof assistantChatRequestSchema>;

const blockRange = z.object({ from: z.string(), to: z.string() });
const blockSeries = z.object({ signalId: objectIdSchema, name: z.string() });

/**
 * One request of a change Dyia prepared. `path` is relative to the organization; `{{n}}` in the
 * path or in the body stands for the id that step `n` (1-based) returned.
 */
export const assistantActionStepSchema = z.object({
  /** What the step does, for the translations of the web app (`dyia.steps.<kind>` with `params`). */
  kind: z.string(),
  params: z.record(z.string()),
  /** The same in English: what the AI reads, and the fallback of the web app. */
  label: z.string(),
  method: z.enum(['POST', 'PATCH', 'PUT', 'DELETE']),
  path: z.string(),
  body: z.unknown().optional(),
});
export type AssistantActionStep = z.infer<typeof assistantActionStepSchema>;

/** What Dyia answers with: text, and things the web app does or draws. */
export const assistantBlockSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), text: z.string() }),
  /** The web app opens this page. `path` is relative to the organization (`/o/:orgId`). */
  z.object({ type: z.literal('navigate'), path: z.string(), label: z.string() }),
  /** A trend the web app draws by querying the signals itself, with the rights of the user. */
  z.object({ type: z.literal('chart'), title: z.string(), range: blockRange, series: z.array(blockSeries).min(1).max(8) }),
  /** Figures over a period, in the display units of the user. */
  z.object({
    type: z.literal('stats'),
    title: z.string(),
    range: blockRange,
    rows: z.array(
      z.object({
        signalId: objectIdSchema,
        name: z.string(),
        unit: z.string().nullable(),
        min: z.number().nullable(),
        max: z.number().nullable(),
        mean: z.number().nullable(),
        last: z.number().nullable(),
      }),
    ),
  }),
  /**
   * Changes Dyia prepared. Nothing is done until the user confirms; the web app then sends the
   * steps itself, in order, as the user. `open` is the page to show once they are done.
   */
  z.object({ type: z.literal('action'), title: z.string(), danger: z.boolean(), steps: z.array(assistantActionStepSchema).min(1).max(12), open: z.string().optional() }),
]);
export type AssistantBlock = z.infer<typeof assistantBlockSchema>;

export const assistantChatResponseSchema = z.object({ blocks: z.array(assistantBlockSchema) });
export type AssistantChatResponse = z.infer<typeof assistantChatResponseSchema>;

/**
 * The chat answers as a stream of lines, one JSON event each (`application/x-ndjson`): text as it
 * is written, blocks as they are made, then `done` with the whole answer — or `error`.
 */
export type AssistantStreamEvent =
  | { t: 'text'; d: string }
  | { t: 'block'; block: AssistantBlock }
  | { t: 'done'; blocks: AssistantBlock[] }
  | { t: 'error'; message: string };

/** Header the web app sets on the requests of a change prepared by Dyia; the audit log says so. */
export const ASSISTANT_VIA_HEADER = 'x-twinlink-via';
